import { Injectable } from '@angular/core'
import { size } from 'lodash'
import { Map } from 'maplibre-gl'

export interface SpriteMetadata {
  [iconName: string]: {
    width: number
    height: number
    x: number
    y: number
    pixelRatio: number
    size?: string
    originalFileName?: string
  }
}

export interface SpriteMasterIndex {
  sprites: {
    [size: string]: {
      sprite: string
      metadata: string
      sizeSuffix: string
    }
  }
  generated: string
  types: string[]
  suffixes: {
    [size: string]: string
  }
}

@Injectable({
  providedIn: 'root',
})
export class SpriteService {
  private map!: Map
  private loadedSprites = new Set<string>()
  private masterIndex: SpriteMasterIndex | null = null
  private spriteMetadata: { [size: string]: SpriteMetadata } = {}

  setMap(map: Map): void {
    this.map = map
  }

  async loadMasterIndex(): Promise<SpriteMasterIndex> {
    if (this.masterIndex) {
      return this.masterIndex
    }

    try {
      const response = await fetch('/assets/sprites/sprites-index.json')
      if (!response.ok) {
        throw new Error(`Failed to fetch sprite index: ${response.status}`)
      }
      this.masterIndex = await response.json()
      console.log('Loaded sprite master index:', this.masterIndex)
      return this.masterIndex!
    } catch (error) {
      console.error('Failed to load sprite master index:', error)
      throw error
    }
  }

  async loadSprites(type: 'small' | 'medium' | 'large' | 'map-icons' = 'medium'): Promise<void> {
    if (this.loadedSprites.has(type)) {
      console.log(`Sprites for ${type} already loaded`)
      return
    }

    try {
      await this.loadMasterIndex()

      if (!this.masterIndex?.sprites[type]) {
        throw new Error(`Sprite type '${type}' not found in master index`)
      }

      const spriteConfig = this.masterIndex.sprites[type]
      const metadataUrl = spriteConfig.metadata
      const spriteUrl = spriteConfig.sprite

      console.log(`Loading ${type} sprites from:`, { metadataUrl, spriteUrl })

      // Load sprite metadata
      const metadataResponse = await fetch(metadataUrl)
      if (!metadataResponse.ok) {
        throw new Error(`Failed to fetch sprite metadata: ${metadataResponse.status}`)
      }
      const spriteMetadata: SpriteMetadata = await metadataResponse.json()

      // Store metadata for later use
      this.spriteMetadata[type] = spriteMetadata

      // Load sprite image
      const spriteImage = await this.loadSpriteImage(spriteUrl)

      // Extract individual icons from sprite and add to map
      let loadedCount = 0
      const iconPromises = Object.keys(spriteMetadata).map(async (iconName) => {
        try {
          const iconData = spriteMetadata[iconName]
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')!

          canvas.width = iconData.width
          canvas.height = iconData.height

          ctx.drawImage(
            spriteImage,
            iconData.x,
            iconData.y,
            iconData.width,
            iconData.height,
            0,
            0,
            iconData.width,
            iconData.height,
          )

          // Convert canvas to ImageBitmap for compatibility with maplibre-gl
          const imageBitmap = await createImageBitmap(canvas)
          this.map.addImage(iconName, imageBitmap)
          loadedCount++
        } catch (error) {
          console.warn(`Failed to process icon ${iconName}:`, error)
        }
      })

      // Wait for all icons to be processed
      await Promise.all(iconPromises)

      this.loadedSprites.add(type)
    } catch (error) {
      throw error
    }
  }

  private async loadSpriteImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = (error) => reject(new Error(`Failed to load sprite image: ${url}`))
      img.src = url
    })
  }

  async loadAllTypes(): Promise<void> {
    const types: Array<'small' | 'medium' | 'large' | 'map-icons'> = [
      'small',
      'medium',
      'large',
      'map-icons',
    ]

    for (const type of types) {
      try {
        await this.loadSprites(type)
      } catch (error) {
        console.warn(`Failed to load sprites for type ${type}, continuing with other types:`, error)
      }
    }
  }

  // Check if a specific icon exists
  hasIcon(iconName: string): boolean {
    return this.map.hasImage(iconName)
  }

  // Get all available icons for a specific size
  getAvailableIcons(size: 'small' | 'medium' | 'large'): string[] {
    const metadata = this.spriteMetadata[size]
    return metadata ? Object.keys(metadata) : []
  }

  getAvailableTypes(): string[] {
    return this.masterIndex?.types || []
  }

  // Get sprite statistics for debugging
  getSpriteStats(): any {
    return {
      loadedSprites: Array.from(this.loadedSprites),
      availableTypes: this.getAvailableTypes(),
      loadedIconCounts: Object.keys(this.spriteMetadata).reduce((acc, size) => {
        acc[size] = Object.keys(this.spriteMetadata[size]).length
        return acc
      }, {} as any),
    }
  }
}
